/**
 * Axios in-flight request deduplication + short-TTL cache for safe GETs.
 *
 * Phase 6 of the N+1 plan:
 *  - In-flight dedup: if two components fire the SAME GET concurrently (common
 *    with React 18 StrictMode double-invoking effects in dev and with nested
 *    pages that both depend on /auth/me), share a single network request.
 *  - Tiny TTL cache on a whitelist of truly-reference-data GETs
 *    (schools, grades, settings, subjects) so re-navigation is instant.
 *
 * All mutating responses (POST/PUT/DELETE/PATCH) invalidate the whole cache
 * defensively — users click rarely enough that an over-broad invalidation is
 * cheaper than tracking per-resource keys on the client.
 *
 * Usage: import and call `installAxiosInterceptors()` once at app bootstrap.
 * All existing `axios.get(...)` calls benefit automatically — no per-caller
 * changes required.
 */
import axios from 'axios';

// Only GETs on these path suffixes are TTL-cached (30s). Everything else is
// still de-duplicated in-flight but re-fetched on every new call.
const CACHEABLE_SUFFIXES = [
  '/api/schools',
  '/api/grades/',       // /api/grades/{school_id}
  '/api/settings/',     // /api/settings/help-number
  '/api/student/subjects',
];
const CACHE_TTL_MS = 30_000;

const inflight = new Map();  // key -> Promise (full axios response)
const ttlCache = new Map();  // key -> { expiresAt, response }

const isCacheable = (url) => {
  if (!url) return false;
  return CACHEABLE_SUFFIXES.some((s) => url.includes(s));
};

const requestKey = (config) => {
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${method}::${url}::${params}`;
};

const invalidateAll = () => {
  ttlCache.clear();
};

export const installAxiosInterceptors = () => {
  // Avoid installing twice in dev hot-reload.
  if (axios.__e1_interceptors_installed) return;
  axios.__e1_interceptors_installed = true;

  axios.interceptors.request.use(async (config) => {
    if ((config.method || 'get').toLowerCase() !== 'get') return config;
    const key = requestKey(config);

    // TTL cache: respond immediately from cache if fresh.
    if (isCacheable(config.url)) {
      const cached = ttlCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        config.adapter = () => Promise.resolve(cached.response);
        return config;
      }
    }

    // In-flight dedup: attach to an existing promise if one exists.
    if (inflight.has(key)) {
      const shared = inflight.get(key);
      config.adapter = () => shared;
      return config;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      const method = (response.config.method || 'get').toLowerCase();
      const key = requestKey(response.config);

      // Record new in-flight result for future concurrent callers.
      // (The promise was stored at call site — see patched `request` below.)
      if (method === 'get') {
        inflight.delete(key);
        if (isCacheable(response.config.url)) {
          ttlCache.set(key, {
            expiresAt: Date.now() + CACHE_TTL_MS,
            response,
          });
        }
      } else {
        // Any mutation invalidates the TTL cache defensively.
        invalidateAll();
      }
      return response;
    },
    (error) => {
      const cfg = error?.config || {};
      const key = requestKey(cfg);
      inflight.delete(key);
      return Promise.reject(error);
    },
  );

  // Wrap axios.request so we can stash the *outer* promise in `inflight`
  // before the request hits the wire. We keep axios's public API untouched.
  const originalRequest = axios.request.bind(axios);
  axios.request = function patchedRequest(config) {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get') return originalRequest(config);

    const key = requestKey(config);
    if (inflight.has(key)) return inflight.get(key);

    if (isCacheable(config.url)) {
      const cached = ttlCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        return Promise.resolve(cached.response);
      }
    }

    const p = originalRequest(config);
    inflight.set(key, p);
    return p;
  };
};

export const clearAxiosCache = invalidateAll;
